import { useMemo, useState } from 'react'
import styled from 'styled-components'
import uniqueId from 'lodash/uniqueId'
import loadImage from 'blueimp-load-image'
import { MAX_PHOTO_SIZE_BYTES } from 'util/constants'

const InvisibleField = styled.input`
  display: none;
`

async function handleFiles(files, imageMaxWidth = 500, outputMimeType = 'image/jpeg', outputExtension = 'jpg') {
  const photos = Array.from(files)
    .filter((file) => file.size < MAX_PHOTO_SIZE_BYTES)
    .map(
      (file) =>
        new Promise((res, rej) => {
          loadImage(file, { canvas: true, orientation: true, maxWidth: imageMaxWidth })
            .then((data) => {
              return new Promise((resolve) => {
                data.image.toBlob((blob) => {
                  data.blob = blob
                  resolve(data)
                }, outputMimeType)
              })
            })
            .then((data) => {
              if (!data.blob) {
                throw new Error('Unable to process photo')
              }

              if (data.blob.size > MAX_PHOTO_SIZE_BYTES) {
                throw new Error('Processed photo exceeds maximum file size')
              }

              const formData = new FormData()
              formData.append('file', data.blob, `new-${file.name.split('.')[0]}.${outputExtension}`)
              const newFile = formData.get('file')
              return res({ file: newFile, dataUrl: URL.createObjectURL(data.blob) })
            })
            .catch(rej)
        })
    )

  return Promise.all(photos)
}

const PhotoField = (props) => {
  const [dataUrl, setDataUrl] = useState(null)

  const handleChange = (event) => {
    if (props.onProcessing) {
      props.onProcessing(event.target.files)
    }
    handleFiles(event.target.files, props.maxWidth, props.outputMimeType, props.outputExtension)
      .then((photos) => {
        const first = photos[0]
        if (first) {
          setDataUrl(first.dataUrl)
        }
        if (props.onChange) {
          const payload = props.multiple ? photos : first
          props.onChange(payload)
        }
      })
      .catch((err) => {
        if (props.onError) {
          props.onError(err)
        }
      })
  }

  const inputId = useMemo(() => props.id || uniqueId('photo-'), [props.id])
  const labelId = `${inputId}-label`

  return (
    <label id={labelId} className={props.className} htmlFor={inputId}>
      {props.children(dataUrl || props.defaultSrc)}
      <InvisibleField
        id={inputId}
        type="file"
        disabled={props.disabled}
        multiple={props.multiple}
        onChange={handleChange}
        onClick={props.onClick}
        accept="image/*"
      />
    </label>
  )
}

export default PhotoField
